import { DAY, WEEK } from '@/constants/calendar';
import { IconCalendar, IconClose } from '@/theme/icons';
import { formatDateLabelByPlan, getLocale } from '@/utils/calendar';
import rem from '@/utils/rem';
import { Button, Flex, HStack, Icon, StyleProps, Text, forwardRef } from '@chakra-ui/react';
import { format } from 'date-fns';
import { Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { ITimezone, useTimezoneSelect } from 'react-timezone-select';

export type DateTimePickerPlaceholderProps = {
  customWidth?: StyleProps['width'];
  placeholder?: string;
  isDatePickerOpen: boolean;
  scheduledDate: Date | null;
  scheduledTime?: Date | null;
  tabIndex: typeof DAY | typeof WEEK;
  onDatePickerOpen: () => void;
  onClickUnplan: () => void;
  border?: StyleProps['border'];
  borderColor?: StyleProps['borderColor'];
  height?: StyleProps['height'];
  selectedTimezone?: ITimezone;
};

function DateTimePickerPlaceholder(
  {
    placeholder,
    isDatePickerOpen,
    scheduledDate,
    scheduledTime,
    tabIndex,
    onDatePickerOpen,
    onClickUnplan,
    border,
    borderColor,
    height = rem(28),
    selectedTimezone,
    customWidth,
  }: DateTimePickerPlaceholderProps,
  ref: Ref<HTMLButtonElement>,
) {
  const { t, i18n } = useTranslation('RPMDateTimePicker');
  placeholder = placeholder ? placeholder : t('placeholders.selectDate');
  const { parseTimezone } = useTimezoneSelect({});
  const abbrev = (selectedTimezone && parseTimezone(selectedTimezone).abbrev) || '';
  const locale = getLocale(i18n.language);

  const dateLabel = scheduledDate
    ? scheduledTime
      ? `${format(new Date(scheduledTime), `E, MMM dd 'at' hh:mm a`, { locale })} ${abbrev}`.trim()
      : formatDateLabelByPlan(tabIndex, scheduledDate, locale)
    : null;

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      gap={rem(8)}
      width={customWidth ?? 'fit-content'}
      height={height}
      border={border}
      borderColor={borderColor}
      borderRadius={rem(4)}
      _hover={{ backgroundColor: 'background-quaternary' }}
      aria-label="Select a date range"
      backgroundColor="background-primary"
      paddingX={rem(8)}
    >
      <Button
        ref={ref}
        width={customWidth}
        height={height}
        padding="0"
        background="transparent"
        _hover={{ background: 'transparent' }}
        aria-expanded={isDatePickerOpen}
        onClick={onDatePickerOpen}
      >
        <HStack width={customWidth} color="text-primary" spacing="2">
          <Icon as={IconCalendar} boxSize={rem(14)} />
          {scheduledDate ? (
            <Text textStyle="small">
              {dateLabel}
            </Text>
          ) : (
            <Text textStyle="small">{placeholder}</Text>
          )}
        </HStack>
      </Button>

      {scheduledDate && (
        <Button
          alignItems="center"
          justifyContent="center"
          display="flex"
          width={rem(16)}
          minWidth={rem(16)}
          height={rem(16)}
          borderRadius="full"
          cursor="pointer"
          backgroundColor="background-tertiary"
          onClick={onClickUnplan}
          variant="unstyled"
        >
          <Icon as={IconClose} boxSize={rem(8)} />
        </Button>
      )}
    </Flex>
  );
}

const ForwardedDateTimePickerPlaceholder = forwardRef(DateTimePickerPlaceholder);

export default ForwardedDateTimePickerPlaceholder;
